import { useLayoutEffect, useRef, useState } from 'react'
import { Patient } from '@medplum/fhirtypes'
import { Tabs } from '@mantine/core'
import { IconListDetails, IconSparkles } from '@tabler/icons-react'

import { usePatientClinicalSummaryOverview } from '@/hooks/usePatientClinicalSummaryOverview'
import { ClinicalSummaryDetails } from './ClinicalSummaryDetails'
import {
  ClinicalSummaryOverview,
  ClinicalSummaryOverviewGeneratedAt,
  ClinicalSummaryOverviewStatus,
} from './ClinicalSummaryOverview'

type Props = {
  patient: Patient
}

// Clinical Summary page: one header row — title (left), Overview/Details tabs
// (middle), the overview's status controls (right, Overview tab only) — above
// the active panel. The overview hook lives here so the header's status
// controls and the Overview panel share one fetch/poll cycle; the AI-generated
// Overview is the default tab, Details is the page's original deterministic
// chart-data view. Panels are not kept mounted, so Details fetches only when
// opened. The page is sized to the viewport space below the chart chrome and
// the active panel scrolls internally, so the header row never scrolls away.
export const ClinicalSummary = ({ patient }: Props) => {
  const [activeTab, setActiveTab] = useState<string | null>('overview')
  const overview = usePatientClinicalSummaryOverview(patient.id)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number>()

  // Measure the viewport space below the chart chrome (navbar + action bar). The chrome height
  // isn't constant — the action bar wraps on narrow widths and its sync badge pops in async —
  // so re-measure on window resizes and on any document reflow.
  useLayoutEffect(() => {
    const measure = () => {
      if (!rootRef.current) return
      // Window scroll carried over from another chart section would skew rect.top; with the
      // panels scrolling internally the window has nothing to scroll anyway.
      window.scrollTo(0, 0)
      setHeight(window.innerHeight - rootRef.current.getBoundingClientRect().top)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return (
    <div ref={rootRef} className="flex flex-col p-4" style={{ height }}>
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        keepMounted={false}
        // Inline styles, not Tailwind classes: Mantine's stylesheet is unlayered while Tailwind
        // utilities live in @layer, so Mantine's own `display: block` on the Tabs root would win
        // over a `flex` class. The flex column lets the active panel fill the remaining height.
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        // The tabs are the page's primary mode switch — heavier than Mantine's
        // default underline tabs: bigger label, semibold, roomier hit target.
        styles={{
          tab: { fontSize: 'var(--mantine-font-size-md)', fontWeight: 600, padding: '10px 20px' },
        }}
      >
        {/* Four header parts — title, tabs, status controls (Overview tab only), and the
            generated stamp. The title→tabs gap is FIXED (so the tabs don't jump when the
            right-side parts appear/disappear on tab change); the status and generated parts
            split the remaining space evenly via their auto margins. When the width can't fit
            everything, parts wrap onto additional lines instead of overlapping — but the
            Tabs.List itself never wraps (Mantine's default flex-wrap stacked the two tabs
            vertically on a narrow pane: call rail / CC-note drawer open). */}
        <div className="mb-4 flex flex-wrap items-center gap-y-2">
          <h1 className="mr-10 shrink-0 text-2xl font-bold whitespace-nowrap">Clinical Summary</h1>
          <Tabs.List className="shrink-0" style={{ flexWrap: 'nowrap' }}>
            <Tabs.Tab value="overview" leftSection={<IconSparkles size={18} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="details" leftSection={<IconListDetails size={18} />}>
              Details
            </Tabs.Tab>
          </Tabs.List>
          {activeTab === 'overview' && (
            <>
              <div className="ml-auto shrink-0 pl-6">
                <ClinicalSummaryOverviewStatus overview={overview} />
              </div>
              <div className="ml-auto shrink-0 pl-6">
                <ClinicalSummaryOverviewGeneratedAt overview={overview} />
              </div>
            </>
          )}
        </div>
        {/* Inline styles for the same layer reason as on Tabs; Mantine appends its own
            `display: none` after these when the panel is inactive. */}
        <Tabs.Panel value="overview" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <ClinicalSummaryOverview overview={overview} />
        </Tabs.Panel>
        {/* Details runs its own two-column scroll (nav rail + scrollable content). */}
        <Tabs.Panel
          value="details"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <ClinicalSummaryDetails patient={patient} />
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}
